import airbridge from 'airbridge-web-sdk-loader';

import {
  APP_ACNE_TRACKING_PARAMS,
  APP_DIET_TRACKING_PARAMS,
  APP_GENERAL_TRACKING_PARAMS,
  APP_HAIRLOSS_TRACKING_PARAMS,
  APP_PUBLIC_DOWNLOAD_URL,
  APP_STORE_PAGE_LINK_TRACKING_PARAMS,
  APP_TRACKING_CHANNEL,
} from '@/constants/common';
import { getEnv } from '@/constants/env';
import { DynamicLinkParams, EAirbridgeLinkType } from '@/shared/types';
import { Client, ClientType } from '@/types/app';
import { infoReport } from '@/utils/report';
import { getErrorObject } from './error';

export interface TrackingLinkOptions {
  campaign?: string;
  ad_group?: string;
  ad_creative?: string;
  content?: string;
  term?: string;
  sub_id?: string;
  sub_id_1?: string;
  sub_id_2?: string;
  sub_id_3?: string;
  deeplink_url: string;
  deeplink_stopover?: boolean;
  fallback_ios?: 'store' | string;
  fallback_android?: 'store' | string;
  fallback_desktop?: string;
  fallback_ios_store_ppid?: string;
  fallback_android_store_listing?: string;
  ogtag_title?: string;
  ogtag_description?: string;
  ogtag_image_url?: string;
  ogtag_website_crawl?: 'desktop';
  custom_short_id?: string;
  is_reengagement?: 'off' | 'on_true' | 'on_false';
  [key: string]: unknown;
}

class Airbridge {
  private static os: 'IOS' | 'ANDROID' | 'WEB' | null = null;

  static initialize() {
    try {
      airbridge.init({
        app: 'doctornow',
        webToken: 'cce91b6cf16e41d59a34f9a1bddda0c1',
        useProtectedAttributionWindow: true,
        protectedAttributionWindowInMinutes: 30,
        utmParsing: true,
      });
    } catch (error) {
      infoReport('Airbridge:initialize > error', {}, getErrorObject(error));
    }
  }
  static trackEvent(eventName: string, eventParams?: Record<string, unknown>) {
    try {
      switch (eventName) {
        case 'page_view':
          airbridge.events.send(eventName, {
            customAttributes: {
              ...eventParams,
              env: getEnv(),
            },
            label: String(eventParams?.page_name),
          });
          break;
        case 'complete_subscription_payment':
          airbridge.events.send(eventName, {
            customAttributes: {
              ...eventParams,
              env: getEnv(),
            },
            value: Number(eventParams?.amountToPay),
          });
          break;
        default:
          airbridge.events.send(eventName, {
            customAttributes: {
              ...eventParams,
              env: getEnv(),
            },
          });
          break;
      }
    } catch (error) {
      infoReport(
        'Airbridge:trackEvent > error',
        {
          eventName,
          eventParams,
        },
        getErrorObject(error)
      );
    }
  }
  static setUserProperties(userProperties: Record<string, unknown>) {
    try {
      Object.entries(userProperties).forEach(([key, value]) => {
        airbridge.setUserAttribute(key, value);
      });
    } catch (error) {
      infoReport(
        'Airbridge:setUserProperties > error',
        {
          userProperties,
        },
        getErrorObject(error)
      );
    }
  }

  static setUserId(userId: string) {
    try {
      airbridge.setUserID(userId);
    } catch (error) {
      infoReport('Airbridge:setUserId > error', { userId }, getErrorObject(error));
    }
  }

  static setClient(client: Client) {
    if (this.os) {
      return;
    }

    if (client.clientType === ClientType.WEB) {
      this.os = 'WEB';
    } else if (client.isIOS) {
      this.os = 'IOS';
    } else if (client.isAndroid) {
      this.os = 'ANDROID';
    }

    this.setUserProperties({
      os: this.os,
    });
  }

  static setDeepLink({
    buttonID,
    deeplinkUrl,
    ctaParams,
  }: {
    buttonID: string;
    deeplinkUrl: string;
    ctaParams?: {
      cta_param_1: string;
      cta_param_2: string;
      cta_param_3: string;
    };
  }) {
    try {
      airbridge.setDeeplinks({
        buttonID: buttonID,
        deeplinks: {
          ios: deeplinkUrl,
          android: deeplinkUrl,
          desktop: deeplinkUrl,
        },
        fallbacks: {
          ios: 'itunes-appstore',
          android: 'google-play',
        },
        ctaParams: ctaParams,
        redirect: true,
        desktopPopUp: true,
      });
    } catch (error) {
      infoReport('Airbridge:setDeepLink > error', {}, getErrorObject(error));
    }
  }

  private static buildDynamicLinkParams(param: DynamicLinkParams) {
    const parsedParams = JSON.parse(JSON.stringify(param));
    return this.os ? { ...parsedParams, ad_group: this.os } : parsedParams;
  }

  static getDynamicLink(param: DynamicLinkParams, linkType: EAirbridgeLinkType = EAirbridgeLinkType.GENERAL) {
    const params = this.buildDynamicLinkParams(param);

    const trackingParams = this.getTrackingParams(linkType);

    const url = `${APP_PUBLIC_DOWNLOAD_URL}/${APP_TRACKING_CHANNEL}?${new URLSearchParams(
      params
    ).toString()}&${trackingParams}`;

    return url;
  }

  static getTrackingParams(linkType: EAirbridgeLinkType = EAirbridgeLinkType.GENERAL) {
    switch (linkType) {
      case EAirbridgeLinkType.STORE_PAGE:
        return APP_STORE_PAGE_LINK_TRACKING_PARAMS;
      case EAirbridgeLinkType.HAIRLOSS:
        return APP_HAIRLOSS_TRACKING_PARAMS;
      case EAirbridgeLinkType.ACNE:
        return APP_ACNE_TRACKING_PARAMS;
      case EAirbridgeLinkType.DIET:
        return APP_DIET_TRACKING_PARAMS;
      case EAirbridgeLinkType.GENERAL:
      default:
        return APP_GENERAL_TRACKING_PARAMS;
    }
  }

  static setDynamicLink(param: DynamicLinkParams, linkType: EAirbridgeLinkType = EAirbridgeLinkType.GENERAL) {
    const url = this.getDynamicLink(param, linkType);
    window.location.href = url;
  }

  static async createTrackingLinkAsync(channel: string, options: TrackingLinkOptions): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      airbridge.createTrackingLink(
        channel,
        options,
        ({ shortURL }) => {
          resolve(shortURL);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }
}

export default Airbridge;
